import React, { useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { errMsg } from "@/components/email/EmParts";

const MODES = [["off", "Off"], ["test", "Test"], ["live", "Live"]];
const TONE = { off: "bg-slate-700 text-white", test: "bg-sky-600 text-white", live: "bg-rose-600 text-white" };

export function ModeControl({ a, canManage, liveAuthorised, onChanged }) {
  const [open, setOpen] = useState(false);
  const [txt, setTxt] = useState("");
  const set = async (mode) => {
    if (mode === a.mode) { return; }
    if (mode === "live") { setOpen(true); return; }
    try { await api.post(`/email-marketing/automations/${a.id}/mode`, { mode }); toast.success(mode === "off" ? "Turned off" : "Test mode: records what it would send"); onChanged(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const goLive = async () => {
    try { await api.post(`/email-marketing/automations/${a.id}/go-live`, { confirm: txt }); toast.success("Live"); setOpen(false); setTxt(""); onChanged(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const locked = !canManage || (!a.can_enable && a.mode === "off");
  return (
    <>
      <div className="inline-flex rounded-lg border border-slate-200 bg-slate-50 p-0.5 shrink-0" data-testid={`auto-mode-${a.key}`}>
        {MODES.map(([m, l]) => (
          <button key={m} type="button" disabled={locked || (m === "live" && !liveAuthorised)} onClick={() => set(m)}
            title={m === "live" && !liveAuthorised ? "Live marketing has not been authorised for this company" : undefined}
            className={`px-2.5 py-1 text-xs font-semibold rounded-md transition-colors disabled:opacity-40 ${a.mode === m ? TONE[m] : "text-slate-600 hover:bg-white"}`}
            data-testid={`auto-mode-${a.key}-${m}`}>{l}</button>))}
      </div>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="auto-golive-dialog">
          <DialogHeader><DialogTitle>Send “{a.name}” to real customers?</DialogTitle>
            <DialogDescription>Real emails go to customers who pass consent, suppression, frequency and quiet-hour checks. Pause All Marketing still overrides this.</DialogDescription></DialogHeader>
          <Input value={txt} onChange={(e) => setTxt(e.target.value)} placeholder="Type GO LIVE" data-testid="auto-golive-input" />
          <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button className="bg-rose-600 hover:bg-rose-700" disabled={txt !== "GO LIVE"} onClick={goLive} data-testid="auto-golive-confirm">Go live</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

export function LiveAuthCard({ s, canManage, onChanged }) {
  const [txt, setTxt] = useState("");
  const call = async (path, body) => {
    try { await api.post(`/email-marketing/automations/${path}`, body); toast.success(path === "revoke-live" ? "Live marketing revoked; live automations back to test" : "Live marketing authorised"); setTxt(""); onChanged(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className={`rounded-xl border px-4 py-3 text-sm flex flex-wrap items-center gap-2 ${s.live_authorised ? "border-rose-200 bg-rose-50" : "border-slate-200 bg-slate-50"}`} data-testid="auto-live-auth">
      <span className="mr-auto" data-testid="auto-live-status">{s.live_authorised
        ? <><b>Live marketing authorised</b> by {s.live_authorisation?.by} — automations still need LIVE switched on one by one.</>
        : <><b>Live sending: not authorised.</b> No automation can send to customers.</>}</span>
      {canManage && (s.live_authorised
        ? <Button size="sm" variant="outline" onClick={() => call("revoke-live")} data-testid="auto-live-revoke">Revoke</Button>
        : <><Input value={txt} onChange={(e) => setTxt(e.target.value)} placeholder="Type AUTHORISE LIVE MARKETING" className="h-8 w-72 bg-white" data-testid="auto-live-auth-input" />
          <Button size="sm" disabled={txt !== "AUTHORISE LIVE MARKETING"} onClick={() => call("authorise-live", { confirm: txt })} data-testid="auto-live-auth-btn">Authorise</Button></>)}
    </div>
  );
}
